import { z } from 'zod';
import { languageSchema } from '../auth/index';

/**
 * Contracten van de module import: één stroom van bestand naar medewerkers.
 *
 * Het oude platform had drie losse CSV-stromen — lidcodes, gegevensvelden en een
 * gebruikerssynchronisatie waarvan niemand wist wat hij precies deed. Dit is er één, en
 * elke stap ervan is zichtbaar voordat er iets geschreven wordt.
 */

// --- De job --------------------------------------------------------------------------

export const importStatusSchema = z.enum([
  'uploaded',
  'mapped',
  'previewed',
  'processing',
  'completed',
  'failed',
]);
export type ImportStatus = z.infer<typeof importStatusSchema>;

/**
 * `full_list` betekent: dit bestand is de volledige personeelslijst, dus wie er niet in
 * staat werkt hier niet meer. Dat is een ingrijpende conclusie, en daarom staat hij
 * standaard uit en vraagt hij een tweede bevestiging met het aantal erbij.
 */
export const importModeSchema = z.enum(['changes_only', 'full_list']);
export type ImportMode = z.infer<typeof importModeSchema>;

export const importActionSchema = z.enum(['create', 'update', 'deactivate', 'skip', 'error']);
export type ImportAction = z.infer<typeof importActionSchema>;

export const importSummarySchema = z.object({
  create: z.number().int(),
  update: z.number().int(),
  deactivate: z.number().int(),
  skip: z.number().int(),
  error: z.number().int(),
});
export type ImportSummary = z.infer<typeof importSummarySchema>;

export const importJobSchema = z.object({
  id: z.guid(),
  fileName: z.string(),
  status: importStatusSchema,
  mode: importModeSchema,
  rowCount: z.number().int(),
  /** Hoeveel rijen er verwerkt zijn; samen met `rowCount` de voortgangsbalk. */
  processedCount: z.number().int(),
  summary: importSummarySchema.nullable(),
  columns: z.array(z.string()),
  mapping: z.record(z.string(), z.string()).nullable(),
  createdBy: z.guid().nullable(),
  createdAt: z.string().datetime(),
  startedAt: z.string().datetime().nullable(),
  finishedAt: z.string().datetime().nullable(),
  errorDetail: z.string().nullable(),
});
export type ImportJob = z.infer<typeof importJobSchema>;

export const importJobListSchema = z.object({ items: z.array(importJobSchema) });
export type ImportJobList = z.infer<typeof importJobListSchema>;

// --- Kolomtoewijzing -----------------------------------------------------------------

/**
 * Waar een kolom uit het bestand naartoe gaat.
 *
 * De vaste doelen staan hier; daarnaast kan een kolom naar een gegevensveld
 * (`field:afdeling`) of naar een groepssoort (`group:department`). Die twee zijn geen
 * enum omdat de klant ze zelf aanmaakt.
 */
export const IMPORT_TARGETS = [
  'employeeNumber',
  'firstName',
  'lastName',
  'email',
  'preferredLanguage',
  'exitDate',
  'role',
] as const;
export type ImportTarget = (typeof IMPORT_TARGETS)[number];

export const mappingTargetSchema = z
  .string()
  .regex(
    /^(employeeNumber|firstName|lastName|email|preferredLanguage|exitDate|role|field:[a-z][a-z0-9_]*|group:(department|shift|line|site|other))$/,
    'Onbekend doelveld.',
  );

/** Kolomnaam → doel. Kolommen die er niet in staan, worden genegeerd. */
export const importMappingSchema = z.record(z.string(), mappingTargetSchema);
export type ImportMapping = z.infer<typeof importMappingSchema>;

export const setMappingRequestSchema = z.object({
  mapping: importMappingSchema,
  mode: importModeSchema.default('changes_only'),
});
export type SetMappingRequest = z.infer<typeof setMappingRequestSchema>;

/**
 * Wat de wizard voorstelt op basis van de kolomnamen in het bestand. Een suggestie is
 * een voorstel en geen keuze: HR ziet hem en kan hem wijzigen.
 */
export const mappingSuggestionSchema = z.object({
  column: z.string(),
  target: z.string().nullable(),
  /** Een voorbeeldwaarde uit de eerste rijen; daarmee herkent HR de kolom sneller. */
  sample: z.string().nullable(),
});
export type MappingSuggestion = z.infer<typeof mappingSuggestionSchema>;

export const mappingOptionsSchema = z.object({
  suggestions: z.array(mappingSuggestionSchema),
  /** Alle doelen waaruit gekozen kan worden, met hun naam in de taal van de gebruiker. */
  targets: z.array(
    z.object({
      value: z.string(),
      label: z.string(),
      /**
       * De naam per taal, bij doelen die de klant zelf benoemt (een gegevensveld). Het scherm toont
       * die van de lezer; `label` is de terugval.
       */
      labels: z.partialRecord(languageSchema, z.string()).optional(),
      group: z.string(),
    }),
  ),
});
export type MappingOptions = z.infer<typeof mappingOptionsSchema>;

export const mappingTemplateSchema = z.object({
  id: z.guid(),
  name: z.string(),
  mapping: importMappingSchema,
  createdAt: z.string().datetime(),
});
export type MappingTemplate = z.infer<typeof mappingTemplateSchema>;

export const createMappingTemplateRequestSchema = z.object({
  name: z.string().min(2).max(80),
  mapping: importMappingSchema,
});
export type CreateMappingTemplateRequest = z.infer<typeof createMappingTemplateRequestSchema>;

export const mappingTemplateListSchema = z.object({ items: z.array(mappingTemplateSchema) });
export type MappingTemplateList = z.infer<typeof mappingTemplateListSchema>;

// --- Voorbeeld en rijen --------------------------------------------------------------

export const importRowSchema = z.object({
  rowNumber: z.number().int(),
  action: importActionSchema,
  employeeNumber: z.string().nullable(),
  displayName: z.string(),
  /** Wat er gaat gebeuren, in één zin — bijvoorbeeld "ploeg wordt Nacht". */
  detail: z.string().nullable(),
  errorCode: z.string().nullable(),
  errorDetail: z.string().nullable(),
});
export type ImportRow = z.infer<typeof importRowSchema>;

export const importRowPageSchema = z.object({
  items: z.array(importRowSchema),
  nextCursor: z.number().int().nullable(),
  summary: importSummarySchema,
});
export type ImportRowPage = z.infer<typeof importRowPageSchema>;

/**
 * Groepen die nog niet bestaan en die de import zou aanmaken. HR ziet ze vóór het
 * bevestigen: een typefout in een kolom "Afdeling" maakt anders stilletjes een groep
 * "Ondrhoud" naast "Onderhoud".
 */
export const previewResultSchema = z.object({
  summary: importSummarySchema,
  newGroups: z.array(z.object({ kind: z.string(), name: z.string() })),
  /** Alleen bij modus `full_list`: hoeveel mensen er gedeactiveerd zouden worden. */
  missingCount: z.number().int(),
});
export type PreviewResult = z.infer<typeof previewResultSchema>;

/**
 * Bevestigen. `expectedDeactivations` moet kloppen met wat het voorbeeld toonde: zo kan
 * een tweede tabblad of een bestand dat intussen wijzigde geen onbedoelde uitdiensten
 * opleveren.
 */
export const commitRequestSchema = z.object({
  expectedDeactivations: z.number().int().min(0).default(0),
});
export type CommitRequest = z.infer<typeof commitRequestSchema>;

// --- Activatiebrieven ----------------------------------------------------------------

export const letterFormatSchema = z.enum(['csv', 'pdf']);
export type LetterFormat = z.infer<typeof letterFormatSchema>;

export const createLettersRequestSchema = z.object({
  format: letterFormatSchema.default('pdf'),
});
export type CreateLettersRequest = z.infer<typeof createLettersRequestSchema>;

export const letterBatchStatusSchema = z.enum(['processing', 'ready', 'failed']);

export const letterBatchSchema = z.object({
  id: z.guid(),
  format: letterFormatSchema,
  /** De brieven worden in de worker gemaakt; de client wacht tot `ready`. */
  status: letterBatchStatusSchema,
  personCount: z.number().int(),
  createdAt: z.string().datetime(),
});
export type LetterBatch = z.infer<typeof letterBatchSchema>;

// --- Upload --------------------------------------------------------------------------

/** Wat de server terugstuurt zodra het bestand gelezen is: kolommen en een paar rijen. */
export const uploadResultSchema = z.object({
  job: importJobSchema,
  options: mappingOptionsSchema,
});
export type UploadResult = z.infer<typeof uploadResultSchema>;

export const importLimitsSchema = z.object({
  maxBytes: z.number().int(),
  maxRows: z.number().int(),
  languages: z.array(languageSchema),
});
export type ImportLimits = z.infer<typeof importLimitsSchema>;
